/* This file defines the TestCaseModal component, which is used to display the testcase comparison modal.
 * This modal is rendered when the instructor clicks on the evaluate or practice marks of a student submission.
 * The modal has two child components, TestCaseList and TestCaseComparison.
 * TestCaseList is used to display the list of testcases.
 * TestCaseComparison is used to display the details of the selected testcase, including input, student output, and the expected output.
 */
import log from "loglevel";
import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import TestCaseComparison from "./TestCaseComparison";
import TestCaseList from "./TestCaseList";
import httpService from 'services/httpService.js';
import Spinner from "react-bootstrap/Spinner";
import * as constants from "Constants";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import { Alert } from "react-bootstrap";

const TestCaseModal = ({
  showModal,
  setShowModal,
  submissionID,
  testcaseType,
  studentName,
  studentEmail,
  obtainedMarks,
  studentID,
}) => {
  // State to store the fetched testcase results
  const [testcaseResults, setTestcaseResults] = useState([]);
  // State to store the selected testcase
  const [selectedTestcase, setSelectedTestcase] = useState(null);
  // State to manage loading status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Function to handle the closing of the modal
  const handleClose = () => setShowModal(false);

  // useEffect to fetch testcase results when the modal is shown
  useEffect(() => {
    const fetchTestcaseResults = async () => {

      // handle case when submissionID is null
      if (submissionID === null || submissionID === undefined) {
        log.error("Submission ID is null");
        setError("No Submission Found for this Student.");
        return;
      }

      setLoading(true);
      let url =
        constants.base_url +
        "/evalpro/submission/compare_output/" +
        submissionID;

      if (studentID) {
        url += `?student_id=${studentID}`;
      }

      try {
        const response = await httpService.get(url);
        if (Array.isArray(response.data.data[testcaseType])) {
          if (response.data.data[testcaseType].length === 0) {
            setError("No Testcase Results Found for this Submission.");
          }
          else {
            setTestcaseResults(response.data.data[testcaseType]);
            setError(null);
          }
        }
      } catch (error) {
        log.error(error?.response);
        if (error?.response?.data?.message) {
          // displayGlobalMessage(error?.response?.data?.message, "error");
          setError(error?.response?.data?.message)
        } else if (error?.response) {
          setError(`${error?.response?.status}: ${error?.response?.statusText}`)
        } else {
          setError("API failed for comparing output", "error")
        }
      } finally {
        setLoading(false);
      }
    };

    if (showModal) {
      fetchTestcaseResults();
    }
  }, [showModal]);

  return (
    <>
      <Modal show={showModal} onHide={handleClose} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {testcaseType === "visible_testcases"
              ? "Visible Testcases"
              : "Hidden Testcases"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div
              className="flex justify-content-center align-items-center"
              style={{ height: "80vh" }}
            >
              <Spinner animation="border" />
            </div>
          ) : error ? (<>
          <div
              className="flex justify-content-center align-items-center"
              style={{ height: "80vh" }}
            >
              <Alert variant="danger">
                {error}
              </Alert>
            </div>
          </>) : (
            <div style={{ display: "flex", height: "80vh" }}>
              {/* Container for the list of testcases */}
              <div style={{ overflow: "auto", height: "100%" }}>
                <TestCaseList
                  testcaseResults={testcaseResults}
                  selectedTestcase={selectedTestcase}
                  setSelectedTestcase={setSelectedTestcase}
                  studentName={studentName}
                  studentEmail={studentEmail}
                  obtainedMarks={obtainedMarks}
                  testcaseType={testcaseType}
                />
              </div>
              {/* Container for the details of the selected testcase */}
              <div
                style={{
                  overflow: "auto",
                  height: "100%",
                  flex: "1",
                  padding: "30px",
                  paddingTop: "0",
                }}
              >
                <TestCaseComparison selectedTestcase={selectedTestcase} />
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TestCaseModal;
